import { useContext, useEffect, useState } from "react";
import UserContext from "../../contexts/UserContext";
import Navbar from "../Navbar";
import PleaseLogin from "./PleaseLogIn";
import {
  CollectionReference,
  DocumentData,
  collection,
  getDocs,
  Query,
  query,
  where,
  QuerySnapshot,
} from "firebase/firestore";
import { RoundsDataObject } from "../../types/RoundsDataObject";
import UserChart from "./UserChart";

/**
 * Renders the "/data" page
 * @returns html containing graphs that show the user's progress in each game mode
 */
const Data = () => {
  useEffect(() => {
    document.title = "Data - FPS Aim Trainer";
  }, []);

  const userContext = useContext(UserContext);
  const [userRoundsData, setUserRoundsData] = useState<RoundsDataObject[]>([]); // Data of user's rounds
  const [gotData, setGotData] = useState(false); // has the data been aquired

  /**
   * Get the user's data
   * @returns the user's data
   */
  const dbConnectAndGetUserData = async () => {
    const roundsData: RoundsDataObject[] = [];
    if (!userContext.db || !userContext.user) return;
    const roundsRef: CollectionReference<DocumentData> = collection(
      userContext.db,
      "rounds"
    );

    const qRounds: Query<DocumentData> = await query(
      roundsRef,
      where("userID", "==", userContext.user.uid)
    );

    const querySnapshotRounds: QuerySnapshot<DocumentData> = await getDocs(
      qRounds
    );

    querySnapshotRounds.forEach((doc) => {
      // doc.data() is never undefined for query doc snapshots
      roundsData.push({
        userID: doc.data().userID,
        timestamp: doc.data().timestamp,
        type: doc.data().type,
        precision: doc.data().precision,
        accuracy: doc.data().accuracy,
        score: doc.data().score,
        difficulty: doc.data().difficulty,
      });
    });
    setUserRoundsData(
      roundsData.sort((r1, r2) => (r1.timestamp > r2.timestamp ? 1 : -1))
    );
    setGotData(true);
  };

  /**
   * Get the user's data if it hasn't already been retrieved
   */
  useEffect(() => {
    if (!gotData) dbConnectAndGetUserData();
  }, [gotData, userContext.db, userContext.user]);

  // info for charts
  const charts = [
    {
      difficulty: "Easy",
      type: "Iron Sights",
    },
    {
      difficulty: "Medium",
      type: "Iron Sights",
    },
    {
      difficulty: "Hard",
      type: "Iron Sights",
    },
    {
      difficulty: "Easy",
      type: "Duels",
    },
    {
      difficulty: "Medium",
      type: "Duels",
    },
    {
      difficulty: "Hard",
      type: "Duels",
    },
  ];

  return (
    <div className="h-screen flex flex-col">
      <Navbar />

      {userContext.loggedIn ? (
        <div className="flex flex-col justify-center items-center pb-4">
          <div className="w-[22rem] sm:w-[35rem] md:w-[45rem] lg:w-[50rem] flex flex-col justify-center items-center gap-10">
            <div className="flex flex-col justify-center items-center pt-4">
              <p className="text-4xl text-indigo-500 font-semibold">
                Rounds Data
              </p>
              <p className="max-w-lg text-gray-800 text-lg pt-4">
                Here is your data for each round you have played. The rounds are
                categorized by the game mode and difficulty, and show your
                score, precision, and accuracy. Your score is defined by the
                number of targets hit. Your precision is the ratio of targets
                hit to projectiles fired. Your accuracy is the average
                projectile's minimum distance to target (lower number implies
                more accurate).
              </p>
            </div>
            {charts.map((chart) => (
              <UserChart
                key={chart.difficulty + chart.type}
                data={userRoundsData.filter(
                  (round) =>
                    round.difficulty === chart.difficulty &&
                    round.type === chart.type
                )}
                label={chart.type + ", " + chart.difficulty}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="w-screen flex-1">
          <PleaseLogin />
        </div>
      )}
    </div>
  );
};

export default Data;
